import {
	faComment,
	faHeadset,
	faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IssueStatus } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { GetHelpWidget } from '../components/dashboard/widgets/GetHelpWidget';
import { EmptyState } from '../components/EmptyState';
import type { HistoryRangeBounds } from '../components/HistoryFilterBar';
import {
	HistoryFilters,
	historyRangeParamUpdates,
	resolveHistoryRange,
} from '../components/HistoryFilterBar';
import { IssueStatusChip } from '../components/IssueStatusChip';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { HistoryListSkeleton } from '../components/skeletons';
import { SupportRequestModal } from '../components/SupportRequestModal';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalIssueService from '../services/portalIssueService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * URL `status` param values → the CRM statuses they stand for. The keys
 * are customer-facing (the portal renders To-Do as "Open", Done as
 * "Resolved" — see IssueStatusChip).
 */
const STATUS_FILTERS: Partial<Record<string, IssueStatus>> = {
	open: IssueStatus.TODO,
	'in-progress': IssueStatus.IN_PROGRESS,
	resolved: IssueStatus.DONE,
};

/**
 * Support requests (PD-13773) — the customer's own submitted issues with
 * case reference + status. The API scopes the list strictly to issues
 * THIS portal user created through the portal; other users' issues and
 * CRM-created issues never appear.
 *
 * PD-15002 adds creating a request right from this page (shared form in
 * a modal), plus status / date-range filtering and date sorting. The
 * list is small and user-scoped, so filter + sort run client-side.
 */
export function SupportPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token, isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isCreateOpen, setIsCreateOpen] = useState(false);

	const statusParam = searchParams.get('status') ?? '';
	const sortParam = searchParams.get('sort') ?? '';
	// Relative range key ('' = no bound; support requests are few, so the
	// default shows all of them — unlike the high-volume history surfaces).
	const rangeParam = searchParams.get('range') ?? '';
	const fromParam = searchParams.get('from') ?? '';
	const toParam = searchParams.get('to') ?? '';

	const { data, isLoading } = useQuery({
		queryKey: ['portal-issues'],
		queryFn: () => portalIssueService.listPortalIssues(token ?? ''),
		enabled: token != null,
		staleTime: 30_000,
	});

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	const allIssues = data?.issues ?? [];

	// Status + date-range filters (client-side; the list is user-scoped and
	// unpaginated). An empty/unknown status param means "all".
	const statusFilter = STATUS_FILTERS[statusParam];
	const rangeBounds: HistoryRangeBounds =
		rangeParam.length > 0
			? resolveHistoryRange(rangeParam, fromParam, toParam)
			: {};
	const { dateFrom, dateTo } = rangeBounds;
	const filteredIssues = allIssues.filter((issue) => {
		if (
			statusFilter != null &&
			(issue.status as IssueStatus) !== statusFilter
		) {
			return false;
		}
		const created = String(issue.createdDate);
		if (dateFrom != null && created < dateFrom) {
			return false;
		}
		if (dateTo != null && created > dateTo) {
			return false;
		}
		return true;
	});

	// Date sort — newest first unless the user picked oldest.
	const sortedIssues = [...filteredIssues].sort((a, b) => {
		const cmp = String(a.createdDate).localeCompare(String(b.createdDate));
		return sortParam === 'oldest' ? cmp : -cmp;
	});

	const hasActiveFilters = statusFilter != null || rangeParam.length > 0;

	const handleCreated = (issueId: number): void => {
		setIsCreateOpen(false);
		// The new request should appear in the list when the user comes back.
		void queryClient.invalidateQueries({ queryKey: ['portal-issues'] });
		void navigate(`/support/${issueId}`);
	};

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Support" backPath="/account" />
			<AccountBreadcrumb current="Support" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-default mb-4">
				{t('Support Requests')}
			</h1>

			{/* Filter tray (status / date range / sort) + New Support Request */}
			<div className="mb-4 flex items-center gap-2">
				<div className="flex-1 min-w-0">
					<HistoryFilters
						fields={[
							{
								kind: 'select',
								key: 'status',
								label: 'Status',
								options: [
									{ value: '', label: 'All Statuses' },
									{ value: 'open', label: 'Open' },
									{ value: 'in-progress', label: 'In Progress' },
									{ value: 'resolved', label: 'Resolved' },
								],
							},
							{
								kind: 'range',
								key: 'range',
								label: 'Date Range',
								defaultRange: 'all',
							},
							{
								kind: 'select',
								key: 'sort',
								label: 'Sort by',
								options: [
									{ value: '', label: 'Newest first' },
									{ value: 'oldest', label: 'Oldest first' },
								],
							},
						]}
						values={{
							status: statusParam,
							range: rangeParam,
							from: fromParam,
							to: toParam,
							sort: sortParam,
						}}
						onApply={(next) => {
							updateParams({
								...historyRangeParamUpdates(next, 'all'),
								status: next.status.length > 0 ? next.status : null,
								sort: next.sort.length > 0 ? next.sort : null,
							});
						}}
					/>
				</div>
				<button
					type="button"
					onClick={() => {
						setIsCreateOpen(true);
					}}
					className="shrink-0 flex items-center gap-2 rounded-lg bg-portal-accent hover:bg-portal-accent-hover px-3 py-2.5 text-sm font-medium text-white min-h-touch"
				>
					<FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
					<span>{t('New Support Request')}</span>
				</button>
			</div>

			{isLoading ? (
				<HistoryListSkeleton />
			) : sortedIssues.length === 0 ? (
				hasActiveFilters ? (
					<EmptyState
						icon={faHeadset}
						title="No support requests match your filters"
						hint="Try clearing the filters to see all of your requests."
						actionLabel="Clear filters"
						onAction={() => {
							updateParams({
								status: null,
								range: null,
								from: null,
								to: null,
							});
						}}
					/>
				) : (
					<EmptyState
						icon={faHeadset}
						title="No support requests yet"
						hint="Create a request and our team will follow up with you."
						actionLabel="New Support Request"
						onAction={() => {
							setIsCreateOpen(true);
						}}
					/>
				)
			) : (
				<div className="flex flex-col gap-2">
					{sortedIssues.map((issue) => (
						<button
							key={issue.id}
							type="button"
							onClick={() => {
								void navigate(`/support/${issue.id}`);
							}}
							className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3 text-left hover:bg-brand-input transition-colors min-h-touch"
						>
							<div className="min-w-0">
								<div className="flex items-center gap-2 flex-wrap">
									<span className="text-sm font-medium text-default truncate">
										{t(issue.name)}
									</span>
									<IssueStatusChip status={issue.status} />
								</div>
								<div className="text-xs text-brand-muted mt-0.5">
									{t('#')}
									{issue.id}
									{issue.type != null && (
										<>
											{' · '}
											{t(issue.type)}
										</>
									)}
									{' · '}
									{formatStatementDate(issue.createdDate as unknown as string)}
								</div>
							</div>
							{issue.messageCount > 0 && (
								<div className="shrink-0 flex items-center gap-1 text-xs text-brand-muted">
									<FontAwesomeIcon icon={faComment} />
									{issue.messageCount}
								</div>
							)}
						</button>
					))}
				</div>
			)}

			{/* Get Help (PD-15235): hours + assigned rep + support contact —
			    the 2.0 home of the legacy shop's Get Help panel, on the page
			    where customers already come for help. Self-hides when nothing
			    is configured. */}
			<div className="mt-6">
				<GetHelpWidget />
			</div>

			<SupportRequestModal
				isOpen={isCreateOpen}
				onClose={() => {
					setIsCreateOpen(false);
				}}
				onCreated={handleCreated}
			/>
		</div>
	);
}
